import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMyMenus } from "@/hooks/useMyMenus";
import { PageHeader, StatCard } from "@/components/app/UI";
import RowDeleteButton from "@/components/app/RowDeleteButton";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Search, Send, CheckCircle2, History, FileText, Camera, X, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  draft: "bg-[#F3F4F6] text-[#0a0a0a]",
  submitted: "bg-[#DBEAFE] text-[#1E40AF]",
  scheduled: "bg-[#DBEAFE] text-[#1E40AF]",
  in_progress: "bg-[#FEF3C7] text-[#92400E]",
  partially_delivered: "bg-[#FEF3C7] text-[#B45309]",
  pending_items: "bg-[#FEE2E2] text-[#991B1B]",
  rescheduled: "bg-[#FED7AA] text-[#9A3412]",
  completed: "bg-[#D1FAE5] text-[#065F46]",
  cancelled: "bg-[#E5E7EB] text-[#4B5563]",
};

export default function DeliveryChecklists() {
  const { user } = useAuth();
  const { menus } = useMyMenus(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checklists, setChecklists] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  // Date-range filter (scheduled_delivery_date). Empty string = unbounded.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cloneFromId, setCloneFromId] = useState(null);
  const [cloneDoc, setCloneDoc] = useState(null);

  // Delivery Checklist permission gate — the single `delivery-checklists`
  // menu key controls ALL actions (view + create + edit + submit + mark
  // items delivered + complete). Admin always allowed regardless.
  const hasChecklistMenu = user?.role === "admin" || (menus ? menus.has("delivery-checklists") : false);
  const isPM = hasChecklistMenu;
  const isDelivery = hasChecklistMenu;

  const load = () => {
    if (!hasChecklistMenu) return; // don't hit the API without permission
    api.get("/delivery-checklists?lite=1").then((r) => setChecklists(r.data));
    api.get("/delivery-checklists/stats/summary").then((r) => setStats(r.data));
  };
  useEffect(() => { load(); }, [hasChecklistMenu]); // eslint-disable-line react-hooks/exhaustive-deps

  // List rows are fetched lite (no items[]) — the clone source is re-fetched
  // in full on demand when the user clicks Copy.
  const cloneFromChecklist = cloneDoc && cloneDoc.id === cloneFromId ? cloneDoc : null;

  // Auto-open a checklist drawer when navigated with ?open=<id>
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && openId !== selected) {
      setSelected(openId);
      // Strip the query param so a manual close doesn't re-open on re-render
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, selected, setSearchParams]);

  // Auto-open the new-checklist editor pre-filled with a project when ?new_for_project=<pid>
  const [newForProject, setNewForProject] = useState(null);
  useEffect(() => {
    const pid = searchParams.get("new_for_project");
    if (pid && !open) {
      setNewForProject(pid);
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new_for_project");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, open, setSearchParams]);

  const [tab, setTab] = useState("today"); // today | schedule | completed
  const todayStr = new Date().toISOString().slice(0, 10);

  // "Closed" statuses are visually terminal — they end up in the Completed
  // tab rather than the schedule.
  const CLOSED_STATUSES = ["completed", "pending_items", "cancelled"];

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return checklists
      .filter((c) => {
        const closed = CLOSED_STATUSES.includes(c.status);
        if (tab === "completed") {
          if (!closed) return false;
        } else if (tab === "today") {
          if (closed) return false;
          if (c.scheduled_delivery_date !== todayStr) return false;
        } else if (tab === "schedule") {
          if (closed) return false;
        }
        // Date-range filter — applied on top of the tab filter. A checklist
        // with no scheduled date is excluded when either bound is set.
        if (dateFrom || dateTo) {
          const sd = c.scheduled_delivery_date;
          if (!sd) return false;
          if (dateFrom && sd < dateFrom) return false;
          if (dateTo && sd > dateTo) return false;
        }
        if (!s) return true;
        return [c.checklist_no, c.project_name, c.project_qr]
          .some((v) => v && String(v).toLowerCase().includes(s));
      })
      // Sort: scheduled_delivery_date ↑, then created_at ↑. Nulls go last.
      .sort((a, b) => {
        const da = a.scheduled_delivery_date || "9999-12-31";
        const db_ = b.scheduled_delivery_date || "9999-12-31";
        if (da !== db_) return da.localeCompare(db_);
        return String(a.created_at || "").localeCompare(String(b.created_at || ""));
      });
  }, [checklists, q, tab, todayStr, dateFrom, dateTo]);

  const counts = useMemo(() => ({
    today: checklists.filter((c) => !CLOSED_STATUSES.includes(c.status) && c.scheduled_delivery_date === todayStr).length,
    schedule: checklists.filter((c) => !CLOSED_STATUSES.includes(c.status)).length,
    completed: checklists.filter((c) => CLOSED_STATUSES.includes(c.status)).length,
  }), [checklists, todayStr]);

  // Route-level access guard — if the current role doesn't have the
  // `delivery-checklists` menu key, block the whole page (matches the sidebar
  // hide behaviour). `menus === null` means we're still loading — show a
  // subtle loading state instead of flashing "no access".
  if (menus !== null && !hasChecklistMenu) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] px-4 text-center" data-testid="delivery-checklists-no-access">
        <div className="label-eyebrow text-[#DC2626] mb-2">Access Denied</div>
        <h2 className="text-2xl font-black tracking-tight mb-2">You don&rsquo;t have access to Delivery Checklists</h2>
        <p className="text-sm text-[#4B5563] max-w-md">
          Your role doesn&rsquo;t include the <b>Delivery Checklists</b> permission. Ask an admin to enable it under
          <b> Admin → Permissions → Delivery Checklists</b>.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="delivery-checklists-page">
      <PageHeader
        eyebrow="Logistics"
        title="Site Delivery Checklist"
        subtitle="PM prepares item lists. Delivery team picks and dispatches from godown."
        action={isPM && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setNewForProject(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11" data-testid="new-checklist-button">
                <Plus className="w-4 h-4 mr-2" /> New Checklist
              </Button>
            </DialogTrigger>
            {open && <ChecklistEditor initialProjectId={newForProject} onDone={() => { setOpen(false); setNewForProject(null); load(); }} />}
          </Dialog>
        )}
      />
      <div className="p-4 sm:p-8 space-y-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard testid="cl-total" label="Total" value={stats.total} />
            <StatCard testid="cl-draft" label="Draft" value={stats.draft} accent="warning" />
            <StatCard testid="cl-submitted" label="Submitted" value={stats.submitted} accent="primary" />
            <StatCard testid="cl-progress" label="In Progress" value={stats.in_progress} accent="warning" />
            <StatCard testid="cl-completed" label="Completed" value={stats.completed} accent="success" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1" data-testid="dcl-tabs">
            {[
              { k: "today", label: `Today (${counts.today})` },
              { k: "schedule", label: `Delivery Schedule (${counts.schedule})` },
              { k: "completed", label: `Completed (${counts.completed})` },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-4 h-9 text-[11px] uppercase tracking-widest font-bold border ${tab === t.k ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
                data-testid={`dcl-tab-${t.k}`}
              >{t.label}</button>
            ))}
          </div>
          <div className="flex gap-3 items-center flex-wrap">
            <div className="relative flex-1 sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project / checklist no / QR…" className="rounded-none h-10 pl-9" data-testid="checklist-search" />
            </div>
            {/* Delivery-date range filter — applies to `scheduled_delivery_date`.
                Both bounds inclusive; leave blank to keep the side unbounded. */}
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-none h-10 w-40"
                data-testid="checklist-date-from"
                title="Delivery date from"
              />
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-none h-10 w-40"
                data-testid="checklist-date-to"
                title="Delivery date to"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="text-[10px] uppercase tracking-widest font-bold text-[#DC2626] hover:underline"
                  data-testid="checklist-date-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Checklist #", "Project", "Delivery", "Includes", "Items", "Status", "Created", "", ""].map((h, i) => <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const delivered = c.delivered_count ?? (c.items || []).filter((i) => i.delivered).length;
                const sched = c.scheduled_delivery_date;
                const closed = CLOSED_STATUSES.includes(c.status);
                const isOverdue = sched && sched < todayStr && !closed;
                const isToday = sched === todayStr && !closed;
                return (
                  <tr key={c.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer" onClick={() => setSelected(c.id)} data-testid={`checklist-row-${c.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{c.checklist_no}</td>
                    <td className="px-4 py-3"><div className="font-semibold">{c.project_name}</div><div className="text-xs text-[#4B5563]">{c.project_qr}</div></td>
                    <td className="px-4 py-3 text-xs">
                      {sched ? (
                        <div>
                          <div className={`font-mono font-semibold ${isOverdue ? "text-[#DC2626]" : isToday ? "text-[#002FA7]" : ""}`}>{sched}</div>
                          {isOverdue && <div className="text-[9px] uppercase tracking-widest text-[#DC2626] font-bold">Overdue</div>}
                          {isToday && <div className="text-[9px] uppercase tracking-widest text-[#002FA7] font-bold">Today</div>}
                        </div>
                      ) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[10px] space-y-0.5">
                      {(c.include_panels || c.panel_brand) && <div><span className="font-bold uppercase tracking-widest text-[#059669]">Panels</span> {c.panel_brand || "—"}{c.panel_capacity_kw ? ` · ${c.panel_capacity_kw} kW` : ""}</div>}
                      {(c.include_inverter || c.inverter_brand) && <div><span className="font-bold uppercase tracking-widest text-[#7C3AED]">Inverter</span> {c.inverter_brand || "—"}{c.capacity_kw ? ` · ${c.capacity_kw} kW` : ""}</div>}
                      {c.include_structure && <div><span className="font-bold uppercase tracking-widest text-[#B45309]">Structure</span></div>}
                      {!c.include_panels && !c.include_inverter && !c.include_structure && !c.panel_brand && !c.inverter_brand && <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">{delivered} / {c.items_count ?? (c.items || []).length}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${STATUS_COLOR[c.status]}`}>{c.status.replace("_", " ")}</span></td>
                    <td className="px-4 py-3 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-right text-[#002FA7] text-xs font-bold uppercase tracking-widest">Open →</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {isPM && (
                        <button
                          onClick={async () => {
                            try {
                              const r = await api.get(`/delivery-checklists/${c.id}`);
                              setCloneDoc(r.data); setCloneFromId(c.id);
                            } catch { toast.error("Failed to load checklist"); }
                          }}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0a0a0a] hover:text-[#002FA7] hover:underline uppercase tracking-widest"
                          data-testid={`checklist-copy-${c.id}`}
                          title="Duplicate checklist for another project"
                        >
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <RowDeleteButton
                        endpoint={`/delivery-checklists/${c.id}`}
                        confirmMessage={`Delete checklist ${c.checklist_no} for "${c.project_name}"? This cannot be undone.`}
                        onDeleted={load}
                        testid={`checklist-delete-${c.id}`}
                        successMessage="Checklist deleted"
                      />
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#9CA3AF] py-12">No checklists yet.</td></tr>}
            </tbody>
          </table>
        </div>

        {selected && (
          <ChecklistDrawer
            id={selected}
            onClose={() => setSelected(null)}
            onChange={load}
            isPM={isPM}
            isDelivery={isDelivery}
            isAdmin={user?.role === "admin"}
          />
        )}

        <Dialog open={!!cloneFromId} onOpenChange={(v) => { if (!v) { setCloneFromId(null); setCloneDoc(null); } }}>
          {cloneFromChecklist && (
            <ChecklistEditor
              cloneFrom={cloneFromChecklist}
              onDone={() => { setCloneFromId(null); setCloneDoc(null); load(); }}
            />
          )}
        </Dialog>
      </div>
    </div>
  );
}

function ChecklistEditor({ checklist, initialProjectId, cloneFrom, onDone }) {
  const [materials, setMaterials] = useState([]);
  const [sets, setSets] = useState([]);
  const [setName, setSetName] = useState("");
  const source = checklist || cloneFrom;
  const [f, setF] = useState({
    project_id: checklist?.project_id || initialProjectId || "",
    items: source?.items?.map((i) => ({ material_id: i.material_id, name: i.name, unit: i.unit, quantity: i.quantity, pm_remarks: i.pm_remarks || "", is_custom: i.is_custom, auto_from: i.auto_from || null })) || [],
    notes: source?.notes || "",
    scheduled_delivery_date: checklist?.scheduled_delivery_date || "",
    include_panels: checklist ? !!checklist.include_panels : false,
    include_inverter: checklist ? !!checklist.include_inverter : false,
    include_structure: checklist ? !!checklist.include_structure : false,
  });
  // "What can still be delivered on a new checklist" — fetched per project.
  // panels_available / inverter_available are false when a prior checklist
  // already delivered that category.
  const [availability, setAvailability] = useState(null);
  useEffect(() => {
    api.get("/purchasing/products").then((r) => setMaterials(r.data.filter((p) => p.active !== false)));
    api.get("/item-sets").then((r) => setSets(r.data));
  }, []);
  useEffect(() => {
    // Only fetch availability when creating a NEW checklist (checklist=null).
    if (checklist || !f.project_id) { setAvailability(null); return; }
    api.get(`/delivery-checklists/project/${f.project_id}/availability`).then((r) => {
      setAvailability(r.data);
      // Auto-uncheck any category that's no longer available so the user
      // starts from a valid state.
      setF((prev) => ({
        ...prev,
        include_panels: prev.include_panels && r.data.panels_available,
        include_inverter: prev.include_inverter && r.data.inverter_available,
      }));
    }).catch(() => setAvailability(null));
  }, [f.project_id, checklist]);

  // Auto-add / auto-remove Panels & Inverter as checklist items when the
  // include-* checkboxes toggle. Prevents the "checklist with no items"
  // gotcha and guarantees Panels/Inverter show up in the delivery run-sheet.
  // Only runs while creating a new checklist — editing is left alone.
  // Selected project object resolved by the picker (optionsEndpoint mode) —
  // drives the auto panel/inverter items. Pre-populated from the checklist
  // itself when editing so no extra fetch is needed.
  const [selectedProject, setSelectedProject] = useState(
    checklist ? {
      id: checklist.project_id,
      panel_brand: checklist.panel_brand,
      inverter_brand: checklist.inverter_brand,
      capacity_kw: checklist.capacity_kw,
      panel_capacity_kw: checklist.panel_capacity_kw,
    } : null
  );
  const buildAutoItem = (key) => {
    if (!selectedProject) return null;
    if (key === "panels") {
      const brand = selectedProject.panel_brand || "Solar Panels";
      const kw = selectedProject.panel_capacity_kw || selectedProject.capacity_kw;
      return {
        material_id: null,
        name: kw ? `Solar Panels — ${brand} (${kw} kW)` : `Solar Panels — ${brand}`,
        unit: "Nos",
        quantity: 1,
        pm_remarks: "",
        is_custom: true,
        auto_from: "panels",
      };
    }
    if (key === "inverter") {
      const brand = selectedProject.inverter_brand || "Inverter";
      const kw = selectedProject.capacity_kw;
      return {
        material_id: null,
        name: kw ? `Inverter — ${brand} (${kw} kW)` : `Inverter — ${brand}`,
        unit: "Nos",
        quantity: 1,
        pm_remarks: "",
        is_custom: true,
        auto_from: "inverter",
      };
    }
    return null;
  };
  useEffect(() => {
    if (checklist) return; // editing existing — don't auto-mess with items
    if (!f.project_id) return;
    setF((prev) => {
      let items = [...prev.items];
      // Sync include_panels
      const hasPanelItem = items.some((it) => it.auto_from === "panels");
      if (prev.include_panels && !hasPanelItem) {
        const it = buildAutoItem("panels");
        if (it) items = [it, ...items];
      } else if (!prev.include_panels && hasPanelItem) {
        items = items.filter((it) => it.auto_from !== "panels");
      }
      // Sync include_inverter
      const hasInverterItem = items.some((it) => it.auto_from === "inverter");
      if (prev.include_inverter && !hasInverterItem) {
        const it = buildAutoItem("inverter");
        if (it) items = [...items, it];
      } else if (!prev.include_inverter && hasInverterItem) {
        items = items.filter((it) => it.auto_from !== "inverter");
      }
      return items === prev.items ? prev : { ...prev, items };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.project_id, f.include_panels, f.include_inverter, selectedProject]);

  const addItem = () => setF({ ...f, items: [...f.items, { material_id: "", name: "", unit: "", quantity: 1, pm_remarks: "", is_custom: false, auto_from: null }] });
  const addCustom = () => setF({ ...f, items: [...f.items, { material_id: null, name: "", unit: "", quantity: 1, pm_remarks: "", is_custom: true, auto_from: null }] });
  const updateItem = (idx, patch) => {
    const copy = [...f.items];
    copy[idx] = { ...copy[idx], ...patch };
    setF({ ...f, items: copy });
  };
  const removeItem = (idx) => setF({ ...f, items: f.items.filter((_, i) => i !== idx) });

  const pickMaterial = (idx, mid) => {
    const m = materials.find((x) => x.id === mid);
    updateItem(idx, { material_id: mid, name: m?.name || "", unit: m?.unit_of_measure || m?.unit || "", is_custom: false });
  };

  // Live per-item stock availability (Physical / Reserved / Available).
  // Fetched debounced whenever an item's (name, brand, unit) or the parent
  // project changes. Keyed by array index so the row's badge stays consistent
  // even when items reorder.
  const [availabilityByIdx, setAvailabilityByIdx] = useState({});
  useEffect(() => {
    if (!f.project_id) { setAvailabilityByIdx({}); return; }
    const timer = setTimeout(async () => {
      const next = {};
      for (let idx = 0; idx < f.items.length; idx++) {
        const it = f.items[idx];
        const name = (it.name || "").trim();
        if (!name || !it.unit) continue;
        let brand = "";
        if (it.auto_from === "panels") brand = selectedProject?.panel_brand || "";
        else if (it.auto_from === "inverter") brand = selectedProject?.inverter_brand || "";
        try {
          const params = { item_name: name, brand, unit: it.unit };
          if (checklist?.id) params.exclude_checklist_id = checklist.id;
          const r = await api.get("/stock/availability", { params });
          next[idx] = r.data;
        } catch { /* silent */ }
      }
      setAvailabilityByIdx(next);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.project_id, JSON.stringify(f.items.map((i) => [i.name, i.unit, i.auto_from, i.quantity]))]);


  const loadSet = (sid) => {
    const s = sets.find((x) => x.id === sid);
    if (!s) return;
    const newItems = s.items.map((i) => ({ material_id: i.material_id, name: i.name, unit: i.unit, quantity: i.quantity, pm_remarks: i.pm_remarks || "", is_custom: i.is_custom }));
    setF({ ...f, items: [...f.items, ...newItems] });
    toast.success(`Loaded ${newItems.length} items from "${s.name}"`);
  };

  const saveAsSet = async () => {
    if (!setName.trim()) { toast.error("Enter a set name"); return; }
    if (!f.items.length) { toast.error("Add items first"); return; }
    try {
      await api.post("/item-sets", { name: setName, items: f.items });
      toast.success(`Saved set "${setName}"`);
      setSetName("");
      api.get("/item-sets").then((r) => setSets(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteSet = async (sid) => {
    if (!window.confirm("Delete this set?")) return;
    await api.delete(`/item-sets/${sid}`);
    api.get("/item-sets").then((r) => setSets(r.data));
  };

  const duplicateSet = async (sid) => {
    const src = sets.find((x) => x.id === sid);
    if (!src) return;
    const base = `${src.name} (Copy)`;
    let candidate = base, n = 2;
    const taken = new Set(sets.map((x) => x.name));
    while (taken.has(candidate)) { candidate = `${base} ${n}`; n += 1; }
    const name = window.prompt(`Duplicate set "${src.name}" as:`, candidate);
    if (!name || !name.trim()) return;
    try {
      const items = src.items.map((i) => ({
        material_id: i.material_id, name: i.name, unit: i.unit,
        quantity: i.quantity, pm_remarks: i.pm_remarks || "", is_custom: i.is_custom,
      }));
      await api.post("/item-sets", { name: name.trim(), items });
      toast.success(`Duplicated as "${name.trim()}"`);
      api.get("/item-sets").then((r) => setSets(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const save = async (submit) => {
    if (!f.project_id) { toast.error("Pick a project"); return; }
    if (!f.scheduled_delivery_date) { toast.error("Scheduled delivery date is required"); return; }
    if (!f.items.length) { toast.error("Add at least one item"); return; }
    for (const it of f.items) {
      if (!it.quantity || it.quantity <= 0) { toast.error("Quantity must be > 0 for all items"); return; }
      if (it.is_custom && (!it.name || !it.unit)) { toast.error("Custom items need name and unit"); return; }
    }
    // Availability guard — warn if any item's required qty exceeds what's
    // available for allocation (physical − everyone-else's-reservation).
    const shortages = f.items
      .map((it, idx) => ({ it, av: availabilityByIdx[idx] }))
      .filter(({ it, av }) => av && Number(it.quantity || 0) > av.available);
    if (shortages.length) {
      const list = shortages.map(({ it, av }) => `• ${it.name} — need ${it.quantity}, available ${av.available}`).join("\n");
      if (!window.confirm(`Some items exceed available stock:\n\n${list}\n\nSave anyway?`)) return;
    }
    try {
      let created;
      if (checklist) {
        created = await api.patch(`/delivery-checklists/${checklist.id}`, f);
      } else {
        created = await api.post("/delivery-checklists", f);
      }
      if (submit) {
        await api.post(`/delivery-checklists/${created.data.id}/submit`);
      }
      toast.success(submit ? "Checklist submitted" : "Saved as draft");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">{checklist ? `Edit ${checklist.checklist_no}` : "New Delivery Checklist"}</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div>
          <Label className="label-eyebrow">Project</Label>
          <div className="mt-2">
            <SearchablePicker
              items={[]}
              value={f.project_id}
              onChange={(id) => setF({ ...f, project_id: id })}
              onSelectItem={(p) => p && setSelectedProject(p)}
              optionsEndpoint="/projects/options"
              disabled={Boolean(checklist)}
              placeholder="Search by customer, QR code or location…"
              testid="checklist-project"
              selectedLabel={(p) => `${p.customer_name} (${p.qr_code})`}
              renderRow={(p) => (
                <div className="flex flex-col text-left w-full">
                  <div className="font-semibold text-sm">{p.customer_name}</div>
                  <div className="text-[10px] text-[#6B7280] font-mono">
                    {p.qr_code}{p.location ? ` · ${p.location}` : ""}
                  </div>
                </div>
              )}
              emptyHint="No matching projects"
            />
          </div>
        </div>

        {availability && (
          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-xs" data-testid="checklist-project-summary">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Info label="Customer" value={availability.customer_name || "—"} />
              <Info label="Phone" value={availability.customer_phone || "—"} />
              <Info label="Panel Brand" value={availability.panel_brand || "—"} />
              <Info label="Inverter Brand" value={availability.inverter_brand || "—"} />
            </div>
            {availability.location && (
              <div className="mt-2 text-[11px] text-[#4B5563]"><b>Location:</b> {availability.location}</div>
            )}
          </div>
        )}

        <div>
          <Label className="label-eyebrow">Scheduled Delivery Date *</Label>
          <Input
            type="date"
            value={f.scheduled_delivery_date}
            onChange={(e) => setF({ ...f, scheduled_delivery_date: e.target.value })}
            className="rounded-none mt-2 h-10 max-w-xs"
            data-testid="checklist-scheduled-date"
          />
        </div>

        {!checklist && (
          <div className="border border-[#E5E7EB] p-3">
            <div className="label-eyebrow mb-2">This Delivery Includes</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className={`flex items-center gap-2 ${availability && !availability.panels_available ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={f.include_panels}
                  disabled={availability && !availability.panels_available}
                  onChange={(e) => setF({ ...f, include_panels: e.target.checked })}
                  className="w-4 h-4 accent-[#002FA7]"
                  data-testid="checklist-inc-panels"
                />
                Solar Panels
                {availability && !availability.panels_available && <span className="text-[10px] text-[#DC2626]">(already delivered)</span>}
              </label>
              <label className={`flex items-center gap-2 ${availability && !availability.inverter_available ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={f.include_inverter}
                  disabled={availability && !availability.inverter_available}
                  onChange={(e) => setF({ ...f, include_inverter: e.target.checked })}
                  className="w-4 h-4 accent-[#002FA7]"
                  data-testid="checklist-inc-inverter"
                />
                Inverter
                {availability && !availability.inverter_available && <span className="text-[10px] text-[#DC2626]">(already delivered)</span>}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={f.include_structure}
                  onChange={(e) => setF({ ...f, include_structure: e.target.checked })}
                  className="w-4 h-4 accent-[#002FA7]"
                  data-testid="checklist-inc-structure"
                />
                Structure Materials
              </label>
            </div>
            <div className="text-[10px] text-[#9CA3AF] mt-2">
              Panels and inverter can only be included once per project. Structure &amp; balance-of-system items can appear on multiple deliveries.
            </div>
          </div>
        )}


        <div className="border border-[#F3F4F6] bg-[#F9FAFB] p-3 flex flex-wrap items-center gap-2">
          <div className="label-eyebrow">Item Sets</div>
          <Select onValueChange={loadSet}>
            <SelectTrigger className="rounded-none h-8 text-xs w-56" data-testid="load-set"><SelectValue placeholder="Load from set…" /></SelectTrigger>
            <SelectContent>{sets.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.items.length})</SelectItem>)}</SelectContent>
          </Select>
          {sets.length > 0 && (
            <Select onValueChange={duplicateSet}>
              <SelectTrigger className="rounded-none h-8 text-xs w-36" data-testid="duplicate-set"><SelectValue placeholder="Duplicate set…" /></SelectTrigger>
              <SelectContent>{sets.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {sets.length > 0 && (
            <Select onValueChange={deleteSet}>
              <SelectTrigger className="rounded-none h-8 text-xs w-32 text-[#EF4444]"><SelectValue placeholder="Delete set…" /></SelectTrigger>
              <SelectContent>{sets.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="flex-1 min-w-[180px] flex gap-1">
            <Input value={setName} onChange={(e) => setSetName(e.target.value)} placeholder="New set name e.g. 3 KW" className="rounded-none h-8 text-xs" data-testid="new-set-name" />
            <Button onClick={saveAsSet} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="save-as-set">Save as Set</Button>
          </div>
        </div>

        <div className="border border-[#E5E7EB]">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Item", "Unit", "Qty", "Remarks", ""].map((h) => <th key={h} className="text-left label-eyebrow !text-[9px] px-2 py-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {f.items.map((it, idx) => (
                <tr key={idx} className={`border-b border-[#F3F4F6] ${it.auto_from ? "bg-[#F0F9FF]" : ""}`}>
                  <td className="px-2 py-2">
                    {it.auto_from ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#002FA7] text-white" data-testid={`item-auto-badge-${idx}`}>
                          {it.auto_from === "panels" ? "Panels" : "Inverter"}
                        </span>
                        <div className="text-xs font-semibold flex-1" data-testid={`item-auto-name-${idx}`}>{it.name}</div>
                      </div>
                    ) : it.is_custom ? (
                      <Input value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} placeholder="Custom item name" className="rounded-none h-8 text-xs" data-testid={`item-name-${idx}`} />
                    ) : (
                      <SearchablePicker
                        items={materials}
                        value={it.material_id}
                        onChange={(v) => pickMaterial(idx, v)}
                        placeholder="Search product…"
                        testid={`item-material-${idx}`}
                        selectedLabel={(p) => p.name}
                        matchItem={productMatch}
                        renderRow={renderProductRow}
                      />
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <Input value={it.unit} onChange={(e) => updateItem(idx, { unit: e.target.value })} disabled={!it.is_custom || !!it.auto_from} placeholder="Unit" className="rounded-none h-8 text-xs w-20" data-testid={`item-unit-${idx}`} />
                  </td>
                  <td className="px-2 py-2">
                    <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: parseFloat(e.target.value) || 0 })} className="rounded-none h-8 text-xs w-20" data-testid={`item-qty-${idx}`} />
                    {availabilityByIdx[idx] && (() => {
                      const a = availabilityByIdx[idx];
                      const short = Number(it.quantity || 0) > a.available;
                      return (
                        <div className={`text-[9px] mt-1 uppercase tracking-widest font-bold ${short ? "text-[#DC2626]" : "text-[#065F46]"}`} data-testid={`item-availability-${idx}`}>
                          {short ? "SHORT " : "OK "} P:{a.physical} R:{a.reserved} A:{a.available}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-2">
                    <Input value={it.pm_remarks} onChange={(e) => updateItem(idx, { pm_remarks: e.target.value })} placeholder="Optional" className="rounded-none h-8 text-xs" data-testid={`item-remarks-${idx}`} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    {it.auto_from ? (
                      <span className="text-[9px] text-[#9CA3AF] uppercase tracking-widest" title="Untick the include checkbox above to remove">Auto</span>
                    ) : (
                      <button onClick={() => removeItem(idx)} className="text-[#EF4444] hover:text-[#DC2626]" data-testid={`item-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {f.items.length === 0 && <tr><td colSpan={5} className="text-center text-xs text-[#9CA3AF] py-6">No items. Add from Product Master or as custom.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="flex gap-2">
          <Button onClick={addItem} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-9" data-testid="add-from-master"><Plus className="w-3 h-3 mr-1" /> Add From Product Master</Button>
          <Button onClick={addCustom} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-9" data-testid="add-custom"><Plus className="w-3 h-3 mr-1" /> Add Custom Item</Button>
        </div>

        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="rounded-none mt-2" data-testid="checklist-notes" />
        </div>

        <div className="flex gap-2 pt-2 border-t border-[#E5E7EB]">
          <Button onClick={() => save(false)} variant="outline" className="flex-1 rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10" data-testid="save-draft">Save Draft</Button>
          <Button onClick={() => save(true)} className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-10" data-testid="submit-checklist"><Send className="w-3 h-3 mr-2" /> Submit</Button>
        </div>
      </div>
    </DialogContent>
  );
}

function ChecklistDrawer({ id, onClose, onChange, isPM, isDelivery, isAdmin }) {
  const [cl, setCl] = useState(null);
  const [audit, setAudit] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  // Local buffer of delivered_quantity inputs so keystrokes stay smooth
  // (we PATCH on blur, not on every character).
  const [qtyBuf, setQtyBuf] = useState({});
  // Pending edits waiting to be saved in a single bulk server call.
  // Shape: { [itemId]: { delivered_quantity?, delivery_remarks?, brand? } }
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingBulk, setSavingBulk] = useState(false);
  // Per-item stock brand buckets (only for non-auto items) so the delivery
  // team can pick which brand is actually being shipped.
  const [brandOpts, setBrandOpts] = useState({});

  const reload = () => {
    api.get(`/delivery-checklists/${id}`).then((r) => {
      setCl(r.data); setQtyBuf({}); setPendingEdits({});
      // Fetch brand availability for every brandless item row.
      (r.data.items || []).forEach((it) => {
        if (it.auto_from || !it.name || !it.unit) return;
        api.get("/stock/availability", { params: { item_name: it.name, brand: "", unit: it.unit } })
          .then((av) => setBrandOpts((prev) => ({ ...prev, [it.id]: av.data.brands || [] })))
          .catch(() => {});
      });
    });
    api.get(`/delivery-checklists/${id}/audit`).then((r) => setAudit(r.data));
  };
  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cl) return null;

  // A checklist is "actionable" (items can be marked) in any of these statuses.
  const ACTIONABLE = ["submitted", "in_progress", "partially_delivered", "scheduled", "rescheduled"];
  const readOnly = cl.status === "completed" || cl.status === "pending_items" || cl.status === "cancelled";
  const canMark = ACTIONABLE.includes(cl.status) && isDelivery;

  // Any pending qty across items (excluding cancelled ones)
  const hasPending = (cl.items || []).some((i) => {
    const st = i.item_status || (i.delivered ? "delivered" : "pending");
    if (st === "cancelled") return false;
    return (Number(i.quantity || 0) - Number(i.delivered_quantity || 0)) > 0;
  });

  // Merge a server item with its pending (unsaved) edits so the UI reflects
  // the operator's typing before the single bulk save fires.
  const effective = (it) => {
    const p = pendingEdits[it.id] || {};
    return {
      delivered_quantity: p.delivered_quantity !== undefined
        ? p.delivered_quantity : Number(it.delivered_quantity || 0),
      delivery_remarks: p.delivery_remarks !== undefined
        ? p.delivery_remarks : (it.delivery_remarks || ""),
      delivered_brand: p.brand !== undefined ? p.brand : (it.delivered_brand || ""),
      dirty: Object.keys(p).length > 0,
    };
  };

  const stageEdit = (itemId, patch) => {
    setPendingEdits((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), ...patch } }));
  };

  const setDeliveredQty = (item, newQty) => {
    const required = Number(item.quantity || 0);
    if (newQty === "" || newQty === null || newQty === undefined) newQty = 0;
    newQty = Number(newQty);
    if (Number.isNaN(newQty) || newQty < 0) { toast.error("Enter a non-negative quantity"); return; }
    if (newQty > required) { toast.error(`Cannot deliver more than required (${required})`); return; }
    stageEdit(item.id, { delivered_quantity: newQty });
  };

  // Choose which stock brand bucket this item ships from. Staged for the
  // single bulk save; backend refunds the old bucket on brand switch.
  const setItemBrand = (item, brand) => {
    stageEdit(item.id, { brand: brand || "" });
  };

  const setItemStatus = async (item, status) => {
    try {
      await api.patch(`/delivery-checklists/${id}/items/${item.id}/status`, {
        item_status: status,
        delivery_remarks: item.delivery_remarks || null,
      });
      toast.success(`Item marked ${status.replace(/_/g, " ")}`);
      reload(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateRemarks = (item, remarks) => {
    if (remarks === (item.delivery_remarks || "")) return;
    stageEdit(item.id, { delivery_remarks: remarks });
  };

  const pendingCount = Object.keys(pendingEdits).length;

  const saveAllPending = async () => {
    if (!pendingCount) return;
    const payload = Object.entries(pendingEdits).map(([itemId, p]) => ({
      item_id: itemId,
      ...(p.delivered_quantity !== undefined ? { delivered_quantity: Number(p.delivered_quantity) } : {}),
      ...(p.delivery_remarks !== undefined ? { delivery_remarks: p.delivery_remarks || null } : {}),
      ...(p.brand !== undefined ? { brand: p.brand || "" } : {}),
    }));
    setSavingBulk(true);
    try {
      await api.post(`/delivery-checklists/${id}/items/bulk-mark`, { items: payload });
      toast.success(`${payload.length} item${payload.length === 1 ? "" : "s"} saved`);
      reload(); onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingBulk(false);
    }
  };

  const discardPending = () => { setPendingEdits({}); setQtyBuf({}); };

  const markAllFullyDelivered = async () => {
    const items = (cl.items || []).filter((it) => {
      const st = it.item_status || (it.delivered ? "delivered" : "pending");
      if (st === "cancelled" || st === "unavailable_low_stock") return false;
      const req = Number(it.quantity || 0);
      const del = Number(it.delivered_quantity || 0);
      return req - del > 0;
    });
    if (!items.length) { toast.message("Nothing pending"); return; }
    const payload = items.map((it) => ({ item_id: it.id, delivered: true }));
    setSavingBulk(true);
    try {
      await api.post(`/delivery-checklists/${id}/items/bulk-mark`, { items: payload });
      toast.success(`${payload.length} item${payload.length === 1 ? "" : "s"} marked delivered`);
      reload(); onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingBulk(false);
    }
  };

  const doComplete = async (createPending) => {
    try {
      const r = await api.post(`/delivery-checklists/${id}/complete`, {
        create_pending_checklist: !!createPending,
      });
      if (createPending && r.data?.pending_child_checklist_id) {
        toast.success("Completed. Pending checklist created.");
      } else if (createPending) {
        toast.success("Completed — no pending items to roll over.");
      } else {
        toast.success("Checklist completed");
      }
      setCompleteOpen(false); reload(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reopen = async () => {
    try {
      await api.post(`/delivery-checklists/${id}/reopen`);
      toast.success("Reopened"); reload(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/delivery-checklists/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cl.checklist_no || "checklist"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate PDF");
    }
  };

  return (
    <Dialog open={Boolean(id)} onOpenChange={(v) => {
      if (!v) {
        if (pendingCount > 0 && !window.confirm(`Discard ${pendingCount} unsaved delivery edit${pendingCount === 1 ? "" : "s"}?`)) return;
        onClose();
      }
    }}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="checklist-drawer">
        <DialogHeader>
          <DialogTitle className="font-black text-2xl tracking-tight flex items-center gap-3">
            {cl.checklist_no}
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${STATUS_COLOR[cl.status]}`}>{cl.status.replace("_", " ")}</span>
            <Button
              onClick={downloadPdf}
              variant="outline"
              className="ml-auto rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8"
              data-testid="checklist-download-pdf"
            >
              <FileText className="w-3 h-3 mr-1" /> Download PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-[#4B5563]">Project: <b>{cl.project_name}</b> · {cl.project_qr}</div>
        <div className="text-xs text-[#4B5563]">Created by {cl.created_by_name} on {fmtDate(cl.created_at)}{cl.submitted_at && ` · Submitted ${fmtDate(cl.submitted_at)}`}{cl.completed_at && ` · Completed ${fmtDate(cl.completed_at)}`}</div>

        {/* Tech specs pinned onto this checklist at creation time. Rendered whenever
            any of the four values is present, so old checklists gracefully degrade. */}
        {(cl.capacity_kw || cl.panel_capacity_kw || cl.inverter_brand || cl.panel_brand) && (
          <div
            className="border border-[#E5E7EB] bg-[#F9FAFB] p-3 mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs"
            data-testid="checklist-tech-specs"
          >
            <div>
              <div className="label-eyebrow !text-[9px] text-[#9CA3AF]">Panel Capacity</div>
              <div className="font-semibold text-[#0a0a0a]" data-testid="checklist-panel-cap">
                {cl.panel_capacity_kw ? `${cl.panel_capacity_kw} kW` : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow !text-[9px] text-[#9CA3AF]">Inverter Capacity</div>
              <div className="font-semibold text-[#0a0a0a]" data-testid="checklist-inv-cap">
                {cl.capacity_kw ? `${cl.capacity_kw} kW` : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow !text-[9px] text-[#9CA3AF]">Panel Brand</div>
              <div className="font-semibold text-[#0a0a0a]" data-testid="checklist-panel-brand">
                {cl.panel_brand || "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow !text-[9px] text-[#9CA3AF]">Inverter Brand</div>
              <div className="font-semibold text-[#0a0a0a]" data-testid="checklist-inv-brand">
                {cl.inverter_brand || "—"}
              </div>
            </div>
          </div>
        )}
        {(cl.site_address || cl.map_link) && (
          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-3 mt-2 text-xs space-y-1" data-testid="checklist-site-info">
            {cl.site_address && (
              <div><span className="label-eyebrow !text-[9px]">Address</span><div className="font-semibold text-[#0a0a0a] mt-0.5">{cl.site_address}</div></div>
            )}
            {cl.map_link && (
              <div className="pt-1">
                <span className="label-eyebrow !text-[9px]">Map</span>
                <div className="mt-0.5">
                  <a href={cl.map_link} target="_blank" rel="noreferrer" className="text-[#002FA7] underline break-all" data-testid="checklist-map-link">{cl.map_link}</a>
                </div>
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="items" className="w-full mt-3">
          <TabsList className="rounded-none border border-[#E5E7EB] bg-white p-0">
            <TabsTrigger value="items" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-4 h-9">Items</TabsTrigger>
            <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-4 h-9"><History className="w-3 h-3 mr-1" /> Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="items" className="mt-4">
            {/* Rescheduled banner — surface reschedule history if it exists. */}
            {(cl.reschedule_history || []).length > 0 && (
              <div className="border-l-4 border-l-[#F59E0B] bg-[#FEF3C7] p-3 mb-3 text-xs" data-testid="reschedule-history">
                <div className="label-eyebrow !text-[9px] text-[#92400E] mb-1">Reschedule History ({cl.reschedule_history.length})</div>
                {cl.reschedule_history.slice(-3).map((h) => (
                  <div key={h.id} className="text-[#92400E]">
                    <b>{h.previous_date || "—"} → {h.new_date}</b> · {h.reason.replace(/_/g, " ")}
                    {h.notes && <span> · {h.notes}</span>}
                    <span className="text-[10px] text-[#B45309]"> · by {h.by_name}, {fmtDate(h.at)}</span>
                  </div>
                ))}
              </div>
            )}
            {cl.parent_checklist_no && (
              <div className="border-l-4 border-l-[#002FA7] bg-[#EFF6FF] p-3 mb-3 text-xs" data-testid="parent-link">
                <div className="label-eyebrow !text-[9px] text-[#1E40AF] mb-1">Rolled over from parent</div>
                <div className="text-[#1E40AF]"><b>{cl.parent_checklist_no}</b> — pending items were carried forward into this checklist.</div>
              </div>
            )}
            {canMark && !readOnly && (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2 border border-[#0a0a0a] bg-[#F9FAFB] px-3 py-2" data-testid="bulk-delivery-bar">
                <div className="flex items-center gap-2 text-xs">
                  {pendingCount > 0 ? (
                    <>
                      <span className="label-eyebrow !text-[9px] text-[#002FA7]">Unsaved</span>
                      <span className="font-bold text-[#002FA7]">{pendingCount} item{pendingCount === 1 ? "" : "s"} pending save</span>
                    </>
                  ) : (
                    <span className="text-[#6B7280]">Edit quantities / brand / remarks, then save all in one call.</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {pendingCount > 0 && (
                    <Button
                      variant="outline"
                      onClick={discardPending}
                      disabled={savingBulk}
                      className="rounded-none border-[#4B5563] text-[#4B5563] uppercase tracking-widest text-xs font-bold h-8"
                      data-testid="discard-pending-edits"
                    >Discard</Button>
                  )}
                  <Button
                    onClick={saveAllPending}
                    disabled={!pendingCount || savingBulk}
                    className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-8 disabled:bg-[#9CA3AF]"
                    data-testid="save-pending-edits"
                  >{savingBulk ? "Saving…" : `Save ${pendingCount || ""} Changes`.trim()}</Button>
                  {hasPending && pendingCount === 0 && (
                    <Button
                      onClick={markAllFullyDelivered}
                      disabled={savingBulk}
                      variant="outline"
                      className="rounded-none border-[#10B981] text-[#065F46] hover:bg-[#D1FAE5] uppercase tracking-widest text-xs font-bold h-8"
                      data-testid="mark-all-delivered"
                    >Mark All Fully Delivered</Button>
                  )}
                </div>
              </div>
            )}
            <div className="border border-[#E5E7EB] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>{["Item", "Unit", "Required", "Delivered", "Pending", "Status", "Actions", "Delivery Remarks"].map((h) => <th key={h} className="text-left label-eyebrow !text-[9px] px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {cl.items.map((it) => {
                    const required = Number(it.quantity || 0);
                    const eff = effective(it);
                    const delivered = Number(eff.delivered_quantity || 0);
                    const pending = Math.max(required - delivered, 0);
                    const serverStatus = it.item_status || (it.delivered ? "delivered" : "pending");
                    // When the user has staged a qty change we preview the
                    // derived status so the badge reflects their intent.
                    let status = serverStatus;
                    if (eff.dirty && pendingEdits[it.id]?.delivered_quantity !== undefined
                        && status !== "cancelled" && status !== "unavailable_low_stock") {
                      status = delivered <= 0
                        ? "pending"
                        : delivered + 1e-6 >= required
                          ? "delivered"
                          : "partially_delivered";
                    }
                    const statusColor = {
                      pending: "bg-[#F3F4F6] text-[#6B7280]",
                      partially_delivered: "bg-[#FEF3C7] text-[#92400E]",
                      delivered: "bg-[#D1FAE5] text-[#065F46]",
                      unavailable_low_stock: "bg-[#FEE2E2] text-[#991B1B]",
                      cancelled: "bg-[#E5E7EB] text-[#4B5563] line-through",
                    }[status] || "bg-[#F3F4F6] text-[#6B7280]";
                    const inputVal = qtyBuf[it.id] !== undefined ? qtyBuf[it.id] : delivered;
                    return (
                      <tr key={it.id} className="border-b border-[#F3F4F6]" data-testid={`drawer-item-${it.id}`}>
                        <td className="px-3 py-2 font-semibold">
                          {it.name}
                          {it.is_custom && <span className="text-[9px] uppercase ml-1 text-[#F59E0B]">custom</span>}
                          {it.pm_remarks && <div className="text-[10px] text-[#4B5563] font-normal mt-0.5">{it.pm_remarks}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">{it.unit}</td>
                        <td className="px-3 py-2 text-xs font-bold">{required}</td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min={0}
                            max={required}
                            step="any"
                            value={inputVal}
                            onChange={(e) => setQtyBuf({ ...qtyBuf, [it.id]: e.target.value })}
                            onBlur={(e) => {
                              const v = Number(e.target.value || 0);
                              if (v !== delivered) setDeliveredQty(it, v);
                              setQtyBuf((b) => { const c = { ...b }; delete c[it.id]; return c; });
                            }}
                            disabled={!canMark || readOnly || status === "cancelled"}
                            className="rounded-none h-8 text-xs w-24"
                            data-testid={`delivered-qty-${it.id}`}
                          />
                          {it.delivered_at && <div className="text-[9px] text-[#4B5563] mt-0.5">{fmtDate(it.delivered_at)} · {it.delivered_by_name}</div>}
                          {/* Brand picker — shown when stock for this item exists
                              under one or more brands (only for non-auto rows). */}
                          {canMark && !readOnly && status !== "cancelled" && !it.auto_from && (brandOpts[it.id] || []).length > 0 && (
                            <Select
                              value={eff.delivered_brand || "_auto"}
                              onValueChange={(v) => setItemBrand(it, v === "_auto" ? "" : v)}
                            >
                              <SelectTrigger className="rounded-none h-7 text-[10px] mt-1 w-36" data-testid={`delivered-brand-${it.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_auto">Brand: Auto</SelectItem>
                                {(brandOpts[it.id] || []).map((b) => (
                                  <SelectItem key={b.brand} value={b.brand}>{b.brand} (on hand {b.physical})</SelectItem>
                                ))}
                                {eff.delivered_brand && !(brandOpts[it.id] || []).some((b) => b.brand === eff.delivered_brand) && (
                                  <SelectItem value={eff.delivered_brand}>{eff.delivered_brand} (0 on hand)</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs font-bold" data-testid={`pending-qty-${it.id}`}>
                          <span className={pending > 0 ? "text-[#DC2626]" : "text-[#065F46]"}>{pending}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-1 whitespace-nowrap ${statusColor}`} data-testid={`item-status-badge-${it.id}`}>
                            {status.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {canMark && !readOnly && (
                            <div className="flex flex-wrap gap-1">
                              {status !== "unavailable_low_stock" && (
                                <button
                                  onClick={() => setItemStatus(it, "unavailable_low_stock")}
                                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-[#DC2626] text-[#DC2626] hover:bg-[#FEE2E2]"
                                  data-testid={`mark-lowstock-${it.id}`}
                                  title="Mark as unavailable due to low stock"
                                >Low Stock</button>
                              )}
                              {status !== "cancelled" && (
                                <button
                                  onClick={() => setItemStatus(it, "cancelled")}
                                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-[#4B5563] text-[#4B5563] hover:bg-[#F3F4F6]"
                                  data-testid={`mark-cancelled-${it.id}`}
                                >Cancel</button>
                              )}
                              {(status === "cancelled" || status === "unavailable_low_stock") && (
                                <button
                                  onClick={() => setItemStatus(it, "pending")}
                                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#F3F4F6]"
                                  data-testid={`mark-pending-${it.id}`}
                                >Reset</button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            key={`rm-${it.id}-${pendingEdits[it.id]?.delivery_remarks !== undefined ? "d" : "s"}`}
                            defaultValue={eff.delivery_remarks || ""}
                            onBlur={(e) => canMark && updateRemarks(it, e.target.value)}
                            disabled={!canMark || readOnly}
                            placeholder={canMark ? "Optional" : "—"}
                            className="rounded-none h-7 text-xs"
                            data-testid={`delivery-remarks-${it.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ChecklistPhotos
              checklistId={id}
              photos={cl.delivery_photos || []}
              canEdit={(isAdmin || isDelivery) && cl.status !== "completed" && cl.status !== "draft"}
              onChange={reload}
            />

            <div className="flex flex-wrap gap-2 mt-4">
              {cl.status === "draft" && isPM && (
                <>
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-9" data-testid="edit-checklist">Edit</Button>
                    </DialogTrigger>
                    <ChecklistEditor checklist={cl} onDone={() => { setEditOpen(false); reload(); onChange(); }} />
                  </Dialog>
                  <Button onClick={async () => { await api.post(`/delivery-checklists/${id}/submit`); toast.success("Submitted"); reload(); onChange(); }} className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-9" data-testid="submit-from-drawer"><Send className="w-3 h-3 mr-1" /> Submit</Button>
                </>
              )}
              {ACTIONABLE.includes(cl.status) && isAdmin && (
                <Button onClick={reopen} variant="outline" className="rounded-none border-[#F59E0B] text-[#F59E0B] uppercase tracking-widest text-xs font-bold h-9" data-testid="reopen-checklist">Reopen as Draft</Button>
              )}
              {cl.status !== "completed" && cl.status !== "cancelled" && (isDelivery || isPM || isAdmin) && (
                <Button onClick={() => setRescheduleOpen(true)} variant="outline" className="rounded-none border-[#F59E0B] text-[#F59E0B] uppercase tracking-widest text-xs font-bold h-9" data-testid="open-reschedule">
                  Reschedule
                </Button>
              )}
              {ACTIONABLE.includes(cl.status) && isDelivery && (
                <Button onClick={() => setCompleteOpen(true)} className="rounded-none bg-[#10B981] hover:bg-[#059669] uppercase tracking-widest text-xs font-bold h-9" data-testid="complete-checklist">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Checklist Complete
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <div className="space-y-1.5 text-xs">
              {audit.map((a) => (
                <div key={a.id} className="border-l-2 border-[#002FA7] pl-3 py-1">
                  <div className="font-semibold">{a.event}</div>
                  <div className="text-[10px] text-[#4B5563]">{fmtDate(a.at)} · {a.by_name} ({a.by_role})</div>
                </div>
              ))}
              {audit.length === 0 && <div className="text-[#9CA3AF]">No history yet.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Complete Checklist dialog — 3 choices when there are pending items. */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg" data-testid="complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-black text-2xl tracking-tight">Complete Checklist</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#4B5563]">
            {hasPending ? (
              <>Some items are pending delivery. Do you want to complete this checklist and create a pending delivery checklist for the remaining items?</>
            ) : (
              <>All items on this checklist are fully delivered. Confirm to close it out.</>
            )}
          </div>
          <div className="flex flex-col gap-2 mt-3">
            {hasPending && (
              <Button
                onClick={() => doComplete(true)}
                className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-10 justify-start px-4"
                data-testid="complete-with-rollover"
              >Complete &amp; Create Pending Checklist</Button>
            )}
            <Button
              onClick={() => doComplete(false)}
              className={`rounded-none bg-[#10B981] hover:bg-[#059669] uppercase tracking-widest text-xs font-bold h-10 justify-start px-4 ${!hasPending ? "" : ""}`}
              data-testid="complete-without-rollover"
            >
              {hasPending ? "Complete Without Creating Pending" : "Confirm Complete"}
            </Button>
            <Button
              onClick={() => setCompleteOpen(false)}
              variant="outline"
              className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10 justify-start px-4"
              data-testid="complete-cancel"
            >Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog — new date + reason (dropdown) + optional notes. */}
      <RescheduleDialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        checklist={cl}
        onDone={() => { setRescheduleOpen(false); reload(); onChange(); }}
      />
    </Dialog>
  );
}

function RescheduleDialog({ open, onOpenChange, checklist, onDone }) {
  const [f, setF] = useState({ new_date: "", reason: "material_unavailable", notes: "" });
  useEffect(() => {
    if (open) setF({ new_date: checklist?.scheduled_delivery_date || "", reason: "material_unavailable", notes: "" });
  }, [open, checklist]);
  if (!checklist) return null;
  const REASONS = [
    { v: "material_unavailable", label: "Material Unavailable" },
    { v: "vehicle_unavailable", label: "Vehicle Unavailable" },
    { v: "customer_unavailable", label: "Customer Unavailable" },
    { v: "site_not_ready", label: "Site Not Ready" },
    { v: "weather", label: "Weather" },
    { v: "other", label: "Other" },
  ];
  const submit = async () => {
    if (!f.new_date) { toast.error("Pick a new date"); return; }
    try {
      await api.post(`/delivery-checklists/${checklist.id}/reschedule`, {
        new_date: f.new_date, reason: f.reason, notes: f.notes || null,
      });
      toast.success("Rescheduled"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-2xl tracking-tight">Reschedule Delivery</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="text-xs text-[#4B5563]">
            Previous date: <b>{checklist.scheduled_delivery_date || "—"}</b>
          </div>
          <div>
            <Label className="label-eyebrow">New Delivery Date</Label>
            <Input type="date" value={f.new_date} onChange={(e) => setF({ ...f, new_date: e.target.value })} className="rounded-none mt-2 h-10" data-testid="reschedule-new-date" />
          </div>
          <div>
            <Label className="label-eyebrow">Reason</Label>
            <Select value={f.reason} onValueChange={(v) => setF({ ...f, reason: v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="reschedule-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Notes (optional)</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="rounded-none mt-2" data-testid="reschedule-notes" placeholder="Any extra context…" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1 rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10" data-testid="reschedule-cancel">Cancel</Button>
            <Button onClick={submit} className="flex-1 rounded-none bg-[#F59E0B] hover:bg-[#D97706] uppercase tracking-widest text-xs font-bold h-10" data-testid="reschedule-confirm">Confirm Reschedule</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChecklistPhotos({ checklistId, photos, canEdit, onChange }) {
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/delivery-checklists/${checklistId}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/delivery-checklists/${checklistId}/photo/${docId}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="mt-6 border-t border-[#E5E7EB] pt-4" data-testid="checklist-photos">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow !text-[10px]">After-Delivery Photos</div>
          <div className="text-[11px] text-[#4B5563]">{photos.length} photo{photos.length === 1 ? "" : "s"} attached to this checklist</div>
        </div>
        {canEdit && (
          <label
            className="cursor-pointer inline-flex items-center gap-1.5 border border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-9 px-3 hover:bg-[#0a0a0a] hover:text-white"
            data-testid="checklist-add-photos"
          >
            <Camera className="w-3 h-3" /> Add Photos
            <input
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="hidden"
              onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
            />
          </label>
        )}
      </div>
      {photos.length > 0 ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {photos.map((docId) => (
            <div key={docId} className="relative border border-[#E5E7EB] bg-[#F9FAFB] overflow-hidden group aspect-square">
              <a href={`${apiBase}/documents/${docId}/download`} target="_blank" rel="noreferrer">
                <img
                  src={`${apiBase}/documents/${docId}/download`}
                  alt="Delivery"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </a>
              {canEdit && (
                <button
                  onClick={() => remove(docId)}
                  className="absolute top-1 right-1 bg-[#EF4444] text-white w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                  data-testid={`remove-checklist-photo-${docId}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-[#9CA3AF] py-6 text-center border border-dashed border-[#E5E7EB]">
          {canEdit ? "No photos yet — use Add Photos to capture proof of delivery" : "No photos attached"}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#9CA3AF]">{label}</div>
      <div className="text-xs font-semibold mt-0.5 truncate" title={value}>{value}</div>
    </div>
  );
}

